import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { type HTMLAttributes, useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

type InputWithSuffixProps = HTMLAttributes<HTMLInputElement> & {
  initialValue?: number;
  unit: string;
  callback?: (value: number) => void;
  min?: number;
  max?: number;
  className?: string;
};

const InputWithSuffix = ({
  initialValue = 0,
  unit,
  callback,
  min = Number.MIN_SAFE_INTEGER,
  max = Number.MAX_SAFE_INTEGER,
  className,
  ...rest
}: InputWithSuffixProps) => {
  const [value, setValue] = useState<number>(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(Number(initialValue));
  }, [initialValue]);

  const setCaretPosition = () => {
    if (inputRef.current) {
      const pos = inputRef.current.value.length - unit.length - 1;
      inputRef.current.setSelectionRange(pos, pos);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.replace(/\D/g, "");
    let numericValue = Number(newValue);
    setValue(numericValue);
    handleDebounceChange(numericValue);
  };

  const handleDebounceChange = useDebouncedCallback((numericValue) => {
    if (numericValue < min) {
      numericValue = min;
    } else if (numericValue > max) {
      numericValue = max;
    }

    setValue(numericValue);
    handleFocus();
    if (callback) {
      callback(Number(numericValue));
    }
  }, 2000);

  useEffect(() => {
    if (document.activeElement === inputRef.current) {
      handleFocus();
    }
  }, [value]);

  const handleFocus = () => {
    setTimeout(setCaretPosition, 0);
  };

  return (
    <Input
      ref={inputRef}
      type="text"
      value={`${value} ${unit}`}
      onChange={handleChange}
      onFocus={handleFocus}
      className={cn("border-0", className)}
      {...rest}
    />
  );
};

export default InputWithSuffix;
